import { produce } from 'immer';
import _ from 'lodash';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import MonthPicker from '../../../../../../common/widgets/MonthPicker';
import {
  Notification,
  NotificationType,
} from '../../../../../../common/widgets/Notification';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { ErrorState, ErrorType } from '../../../../models/Errors';
import { PartialReportData } from '../../../../models/FinalReportItem';
import { resources } from '../../../../resources/resources';
import { finalReportsService } from '../../../../services/final-reports';
import { FinalReportState } from '../FinalReportState';
import { CompanyItem, SelectGroupTypes, toSelectedGroupString } from '../types';

interface GenerateReportDialogProps {
  onCloseGenerateDialog: () => void;
  state: FinalReportState;
  setState: Dispatch<SetStateAction<FinalReportState>>;
}

export default function GenerateReportDialog(
  props: Readonly<GenerateReportDialogProps>,
) {
  const { t: translate } = useTranslation();
  const { onCloseGenerateDialog, state, setState } = props;
  const [errors, setErrors] = useState<ErrorState>({});
  const { customerInfo } = useContext(CustomerInfoContext);
  const [date, setDate] = useState<Date>(new Date());
  const defaultCompanyValue = {
    id: 0,
    name: translate(resources.admin.finalReport.allCompanies),
  };
  const [company, setCompany] = useState<CompanyItem>(defaultCompanyValue);

  const handleGenerateReport = () => {
    (async () => {
      setState(
        produce(draft => {
          draft.isGeneratingReport = true;
        }),
      );

      const companyId = (() => {
        if (state.isOneCompany) {
          return state.companies[0].id;
        }
        if (company.id === 0) {
          return null;
        }
        return company.id;
      })();

      const clientId =
        company.id === 0 && !state.isOneCompany ? customerInfo.clientId : null;

      const selectGroup =
        company.id === 0
          ? toSelectedGroupString[SelectGroupTypes.ForAllCompanies]
          : toSelectedGroupString[SelectGroupTypes.OnlyForThisCompany];

      const data: PartialReportData = {
        companyId,
        clientId,
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        selectGroup,
      };

      const response = await finalReportsService.createPartialReport(data);
      if (response.hasErrors) {
        if (response.errorType === ErrorType.ValidationError) {
          setValidationErrors(response, setErrors);
        } else {
          showErrorToast(
            translate(resources.admin.finalReport.generate.errorMessage),
          );
        }
      } else {
        setState(
          produce(draft => {
            draft.reports.dataState = {
              ...draft.reports.dataState,
              skip: 0,
            };
            draft.needReload = new Date().toUTCString();
          }),
        );
        showSuccessToast(
          translate(resources.admin.finalReport.generate.successMessage),
        );
      }
      setState(
        produce(draft => {
          draft.isGeneratingReport = false;
          draft.showGenerateReportDialog = false;
        }),
      );
    })();
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  const handleDateChanged = (e: any) => {
    setDate(e.value || new Date());
  };

  const handleCompanyChange = (e: any) => {
    setCompany(e.target.value);
  };

  return (
    <>
      {hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Error}
          messages={errors.null.map(e => ({
            message: getErrorMessage(e, translate),
            onShowDetailsClick: undefined,
          }))}
        ></Notification>
      )}
      <Dialog
        closeDialog={onCloseGenerateDialog}
        showCloseIcon
        closeOnClickOutside={false}
        className="dialog-m attendence-settings-dialog"
      >
        <div className="dialog-content">
          <div className="dialog-title">
            {translate(resources.admin.finalReport.generate.action)}
          </div>

          <MonthPicker
            value={date}
            label={translate(resources.admin.finalReport.choosePeriod)}
            onChange={handleDateChanged}
            errors={errors?.month ?? errors?.year}
            clearErrors={() => {
              clearErrors('month');
              clearErrors('year');
            }}
          ></MonthPicker>
          {!state.isOneCompany && (
            <Dropdown
              value={company}
              textField="name"
              dataItemKey="id"
              defaultValue={defaultCompanyValue}
              label={translate(resources.admin.finalReport.chooseCompany)}
              data={
                state.companies?.length
                  ? _.orderBy([...state.companies, defaultCompanyValue], 'id')
                  : ([] as CompanyItem[])
              }
              onChange={handleCompanyChange}
              errors={errors?.selectedGroup}
              clearErrors={() => clearErrors('selectedGroup')}
            ></Dropdown>
          )}
        </div>

        <div className="dialog-actions horizontal">
          <Button onClick={onCloseGenerateDialog} className="btn btn-outline">
            {translate(resources.cancel)}
          </Button>
          <Button
            className="btn btn-solid"
            onClick={handleGenerateReport}
            loading={state.isGeneratingReport}
          >
            {translate(resources.save)}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
